import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Snowflake, QrCode, CalendarDays, FileText, LogOut, ChevronRight } from "lucide-react";
import { isStaffLoggedIn, clearStaffPin } from "@/lib/staffAuth";

const StaffHub = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isStaffLoggedIn()) navigate("/staff/login", { replace: true });
  }, [navigate]);

  const logout = () => {
    clearStaffPin();
    navigate("/staff/login", { replace: true });
  };

  const today = new Date().toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });

  const Tile = ({ to, icon: Icon, color, title, sub, testId }) => (
    <Link
      to={to}
      data-testid={testId}
      className="group flex items-center gap-4 bg-white rounded-2xl p-5 shadow-sm border border-slate-100 active:scale-[0.99] active:bg-slate-50 transition"
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${color} flex-shrink-0`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h3
          className="text-lg font-semibold text-slate-900 leading-tight"
          style={{ fontFamily: "Playfair Display" }}
        >
          {title}
        </h3>
        <p className="text-sm text-slate-500 mt-0.5">{sub}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-sky-500" />
    </Link>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-sky-50">
      {/* Header */}
      <header className="px-5 pt-8 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center">
            <Snowflake className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Playfair Display" }}>
              Mitarbeiter
            </h1>
            <p className="text-xs text-slate-500">{today}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="text-slate-400 hover:text-red-500 p-2"
          data-testid="staff-logout-btn"
          aria-label="Abmelden"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* Tiles */}
      <main className="px-5 py-6 space-y-3 max-w-md mx-auto">
        <Tile
          to="/staff/scanner"
          icon={QrCode}
          color="bg-emerald-500"
          title="QR-Scanner"
          sub="Tickets & Gutscheine einlösen"
          testId="tile-scanner"
        />
        <Tile
          to="/staff/tagesplan"
          icon={CalendarDays}
          color="bg-sky-500"
          title="Tagesplan"
          sub="Heutige Sessions im Überblick"
          testId="tile-tagesplan"
        />
        <Tile
          to="/staff/schichtbericht"
          icon={FileText}
          color="bg-amber-500"
          title="Schichtbericht"
          sub="Schicht beenden · PDF & E-Mail"
          testId="tile-schichtbericht"
        />

        <div className="pt-8 text-center">
          <p className="text-xs text-slate-400">
            Tipp: Im Browser-Menü „Zum Startbildschirm hinzufügen" wählen, um die App
            wie eine echte App zu nutzen.
          </p>
        </div>
      </main>
    </div>
  );
};

export default StaffHub;
